import { defineStore } from "pinia";
import { usePaymentStore } from "%/dynamic-component-editor/stores/payments-store";
import { $axios } from "@bloglovin/vue-framework";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import { SIGN_UP_SETTINGS_MAP } from "@/constants/sign-up-settings-map";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import Publication from "%/dynamic-component-editor/classes/publication";
import useDynamicComponentStore from "%/dynamic-component-editor/stores/dynamic-component-store";
import { Context } from "%/dynamic-component-editor/classes/context";

export const useConfigurationStore = defineStore("configuration-store", {
  state: () => ({
    $_configuration: {}, // Private state variable for configuration data
    isLoadingUpdateConfiguration: false,
    isLoadingPublishConfigToProduction: false,
    isLoadingCreateSubscriptionPlan: false,
    publicationId: 0,
  }),
  getters: {
    selectedAccessControlTypeKey() {
      const foundSetting = Object.entries(SIGN_UP_SETTINGS_MAP).find(
        ([, setting]) => {
          return (
            setting.features.canSignUp === this.canSignUp &&
                setting.features.isPrivatePublication === this.isPrivatePublication &&
                setting.features.requireAccountApproval === this.requireAccountApproval &&
                setting.features.requirePaidSubscription === this.requirePaidSubscription
          );
        },
      );
      return foundSetting ? foundSetting[0] : null;
    },
    selectedAccessControlTypeOption() {
      return SIGN_UP_SETTINGS_MAP[this.selectedAccessControlTypeKey];
    },
    dateFormat() {
      return this.$_configuration.dateFormat;
    },
    areSubscriptionsAvailable() {
      return this.canSignUp && this.availableSubscriptionPlans.length > 0;
    },
    canCreateQuickPost() {
      return this.ugcInterface === "quickPost";
    },
    canCreateArticle() {
      return this.ugcInterface === "article";
    },
    isSignupAndLoginEnabled() {
      return this.canSignUp || this.isPrivatePublication;
    },
    designVariant() {
      return this.$_configuration.designVariant;
    },
    primaryNavigation() {
      return this.$_configuration.primaryNav;
    },
    availableSubscriptionPlans() {
      return this.$_configuration.availableSubscriptionPlans;
    },
    canVoteOnPosts() {
      return this.$_configuration.canVoteOnPosts;
    },
    canLikePosts() {
      return this.$_configuration.canLikePosts;
    },
    commentModerationStrategy() {
      return this.$_configuration.commentModerationStrategy;
    },
    commentReplyDisplayStyle() {
      return this.$_configuration.commentReplyDisplayStyle;
    },
    digitalProductsVisible() {
      return this.$_configuration.digitalProductsVisible;
    },
    isShopEnabled() {
      return this.$_configuration.isShopEnabled;
    },
    isSubscriptionsEnabled() {
      return this.$_configuration.isSubscriptionsEnabled;
    },
    isEventsEnabled() {
      return this.$_configuration.isEventsEnabled;
    },
    isAppStoreCheckoutEnabled() {
      return this.$_configuration.appStoreCheckoutEnabled;
    },
    isShowsEnabled() {
      return this.$_configuration.isShowsEnabled;
    },
    isChatEnabled() {
      return this.$_configuration.isChatEnabled;
    },
    isCollaborationEnabled() {
      return this.$_configuration.isCollaborationEnabled;
    },
    isNewsletterEnabled() {
      return this.$_configuration.isNewsletterEnabled;
    },
    isProgramsEnabled() {
      return this.$_configuration.isProgramsEnabled;
    },
    commentReplyNestingLimit() {
      return this.$_configuration.commentReplyNestingLimit;
    },
    canLikeComments() {
      return this.$_configuration.canLikeComments;
    },
    isPrivatePublication() {
      return this.$_configuration.isPrivatePublication;
    },
    requireAccountApproval() {
      return this.$_configuration.requireAccountApproval;
    },
    requirePaidSubscription() {
      return Boolean(this.$_configuration.requirePaidSubscription);
    },
    ugcInterface() {
      return this.$_configuration.ugcInterface;
    },
    freeCommunityType() {
      return this.$_configuration.freeCommunityType;
    },
    exclusiveCommunityType() {
      return this.$_configuration.exclusiveCommunityType;
    },
    directMessaging() {
      return this.$_configuration.directMessaging;
    },
    commentDefaultSortingStrategy() {
      return this.$_configuration.commentDefaultSortingStrategy;
    },
    ugcModerationStrategy() {
      return this.$_configuration.ugcModerationStrategy;
    },
    ugcMaxLength() {
      return this.$_configuration.ugcMaxLength;
    },
    ugcAllowMedia() {
      return this.$_configuration.ugcAllowMedia;
    },
    ugcWhoMayPost() {
      return this.$_configuration.ugcWhoMayPost;
    },
    canCommentOnPosts() {
      return this.$_configuration.canCommentOnPosts;
    },
    canSavePosts() {
      return this.$_configuration.canSavePosts;
    },
    canReplyToComments() {
      return this.$_configuration.canReplyToComments;
    },
    canVoteOnComments() {
      return this.$_configuration.canVoteOnComments;
    },
    canSignUp() {
      return this.$_configuration.canSignUp;
    },
    canUserCreatePost() {
      return this.ugcInterface !== "none";
    },
    dynamicComponents() {
      if (this.$_configuration.dynamicComponents === undefined) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(this.$_configuration.dynamicComponents).filter(([, component]) => component.dynamic_component_type === "standard-component"),
      );
    },
    customDynamicComponents() {
      if (this.$_configuration.dynamicComponents === undefined) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(this.$_configuration.dynamicComponents).filter(([, component]) => {
          return component.dynamic_component_type === "custom-component" && !component["dynamic-app-id"];
        }),
      );
    },
    dynamicPages() {
      if (this.$_configuration.dynamicComponents === undefined) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(this.$_configuration.dynamicComponents).filter(([, component]) => component.dynamic_component_type === "standard-page"),
      );
    },
    dynamicApps() {
      return this.$_configuration.apps ?? {};
    },
    customDynamicPages() {
      if (this.$_configuration.dynamicComponents === undefined) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(this.$_configuration.dynamicComponents).filter(([, component]) => {
          return component.dynamic_component_type === "custom-page" && !component["dynamic-app-id"];
        }),
      );
    },
    brandingStyleId() {
      return this.$_configuration.styleId;
    },
    brandingStyleVersionId() {
      return this.$_configuration.styleVersionId;
    },
    dynamicComponentBreakpoints() {
      return (this.$_configuration.dynamicComponentBreakpoints || []).map(breakpoint => {
        return {
          ...breakpoint,
          alias: breakpoint.alias || breakpoint.value,
        };
      });
    },
  },
  actions: {
    setBrandingStylesVersionId(styleVersionId) {
      this.$_configuration.styleVersionId = styleVersionId;
    },
    addDynamicComponent(componentConfigData) {
      // Studio's bootstrap leaves $_configuration as an empty object —
      // the legacy `dynamicComponents` map only hydrates in FanWeb /
      // Develop where the publication-configuration payload is injected.
      // Without this guard, the first preload-and-create roundtrip from
      // Feed.vue (StoryCardSmall, EventCard, …) throws
      // "Cannot set properties of undefined" against the bare proxy.
      if (!this.$_configuration.dynamicComponents) {
        this.$_configuration.dynamicComponents = {};
      }
      this.$_configuration.dynamicComponents[componentConfigData.dynamic_component_id] = componentConfigData;
    },
    getDynamicComponentConfigurationByName(componentName) {
      return Object.values(this.$_configuration.dynamicComponents ?? {}).find(component => component.dynamic_component_name === componentName);
    },
    getDynamicComponentConfigurationById(componentId) {
      return Object.values(this.$_configuration.dynamicComponents ?? {}).find(component => component.dynamic_component_id === componentId);
    },
    setPublicationId(publicationId) {
      this.publicationId = publicationId;
    },
    installAppOnPublication(appId) {
      const apiUri = useGeneralStore().getLink(links.api);
      $axios
          .post(
            `${apiUri}/publication/${this.publicationId}/app/install`,
            { app_id: appId },
          )
          .then(response => {
            const publication = Publication.get(this.publicationId);
            publication.version = response.data.publicationVersionId;
            this.$_configuration = response.data.publicationConfiguration;
            const appKeys = Object.keys(response.data.appConfiguration);
            Context.addSource(appKeys[0], response.data.appConfiguration[appKeys[0]]);
            useDynamicComponentStore().addDynamicComponents(response.data.componentConfigurations);
          });
    },
    setPublicationStagingVersionId(publicationStagingVersionId) {
      Publication.get(this.publicationId).version = publicationStagingVersionId;

      if (window.parent && window !== window.parent) {
        const newId = publicationStagingVersionId;
        if (window.lastSentId !== newId) {
          window.lastSentId = newId;
          window.parent.postMessage(
            {
              type: "publication_version_id_update",
              publicationStagingVersionId: newId,
            },
            "*",
          );
        }
      }
    },
    updateConfiguration(newConfigData, success = () => () => {}) {
      this.isLoadingUpdateConfiguration = true;
      const apiUri = useGeneralStore().getLink(links.api);
      const publication = Publication.get(this.publicationId);
      $axios
          .post(
            `${apiUri}/publication/${this.publicationId}/version/${publication.version}/update`,
            { config_data: newConfigData },
          )
          .then(response => {
            this.$_configuration = response.data.configuration;
            publication.version = response.data.publicationVersionId;
            success();
          })
          .catch(error => {
            if (error?.response?.data?.message === "staging_version passed does not match current staging version of publication") {
              useDynamicComponentEditorStore().showVersionMismatchModal = true;
            }
            useDynamicComponentEditorStore().savingMessage = "Error saving";
            useDynamicComponentEditorStore().errorUpdatingModule = true;
          })
          .finally(() => {
            this.isLoadingUpdateConfiguration = false;
          });
    },
    publishConfigToProduction(memo, successCallback, errorCallback) {
      this.isLoadingPublishConfigToProduction = true;
      const publication = Publication.get(this.publicationId);
      this.apiPost(`/publication/${this.publicationId}/version/${publication.version}/publish`,
        {
          memo,
        },
        data => {
          this.$_configuration = data.configuration;
          publication.version = data.publicationVersionId;

          if (successCallback) {
            successCallback();
          } else {
            window.location.reload();
          }
        },
        error => {
          if (errorCallback) {
            let message = "An error occurred while publishing the configuration.";
            if (error?.response?.data?.exception?.includes("OutdatedPublicationVersionException")) {
              message = "The publication version is outdated. Please refresh the page and try again.";
            }
            errorCallback(message);
          }
        },
        () => {
          this.isLoadingPublishConfigToProduction = false;
        });
    },
    createSubscriptionPlan(
      name,
      description,
      billingFrequency,
      price,
      currencyCode,
      accessRevocationGracePeriod,
      exclusivityWindow,
      successCallback = () => {
      },
    ) {
      this.isLoadingCreateSubscriptionPlan = true;
      const paymentStore = usePaymentStore();
      const generalStore = useGeneralStore();
      const publication = Publication.get(this.publicationId);
      return $axios
          .post(
            `/subscription/publication/${this.publicationId}/version/${publication.version}`,
            {
              name,
              description,
              billing_frequency: billingFrequency,
              price,
              currency_code: currencyCode,
              access_revocation_grace_period_days: accessRevocationGracePeriod,
              exclusivity_window_days: exclusivityWindow,
            },
          )
          .then(async response => {
            const data = response.data;
            successCallback(data);
            this.$_configuration = data.configuration;

            const newPlan =
                data.activeSubscriptionPlans[data.activeSubscriptionPlans.length - 1];

            await paymentStore.createSubscriptionProductAndPrice(
              newPlan.name,
              newPlan.description,
              newPlan.price * 100,
              newPlan.currency_code,
              newPlan.billing_frequency,
              newPlan.subscription_plan_id,
              generalStore.userId,
              generalStore.orgId,
              newPlan.publication_id,
              generalStore.orgId,
              true,
            );

            return data;
          })
          .finally(() => {
            this.isLoadingCreateSubscriptionPlan = false;
          });
    },
    setConfiguration(configuration) {
      this.$_configuration = configuration;
    },
    addDynamicComponentToConfiguration(dynamicComponentId, dynamicComponent) {
      this.$_configuration.dynamicComponents[dynamicComponentId] = dynamicComponent;
    },
    setDesignVariant(variant) {
      this.$_configuration.designVariant = variant;
    },
  },
});
